/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigState, CountryData, LibraryData, ProductData, UIState } from '../store/interfaces';
import { translate } from '../i18n';
import { ReactComponent as IconArrowLeft } from '../icons/arrow_left.svg';
import { ReactComponent as IconArrowRight } from '../icons/arrow_right.svg';

import styles from '../styles/additionalProducts.module.scss'
import { getPricingText } from '../helpers/pricing';
import { PreviewContainer, PreviewContainerModel } from './Preview';
import { getMinimumOrderAmount } from '../helpers/helpers';

interface AdditionalProductsProps {
    config: ConfigState;
    ui: UIState;
    library: LibraryData;
    scrollRef?: React.MutableRefObject<HTMLDivElement>;
    eventRef?: React.MutableRefObject<HTMLDivElement>;
    onProductAdd: (article: string, size: string, ventilationZipper: boolean) => void;
}

interface ProductProps {
    article: ProductData;
    index: number;
    country: CountryData;
    library: LibraryData;
    config: ConfigState;
    divRef: React.RefObject<HTMLDivElement>;
    onProductAdd: (article: string, size: string, ventilationZipper: boolean) => void;
}


function Product(props: ProductProps) {
    const {t, i18n} = useTranslation();
    return <div className={styles.product} key={props.index}>
        <div className={styles.productPreview}>
            <div className={styles.productPreviewContainer} ref={props.divRef}>
            </div>
        </div>
        <h1>{translate(props.article.name, i18n)}</h1>               
        {props.article.hasVentilationZipper && <h2>{t('editscreen.invVentilationShort')}</h2>}
        <p dangerouslySetInnerHTML={{__html: t('additionalProducts.price', {price: getPricingText(i18n.language, props.country, props.article.pricing[0].pricing)})}}></p>
        <p dangerouslySetInnerHTML={{__html: t('additionalProducts.fromAmount', {pieces: getMinimumOrderAmount(props.article, props.library)})}}></p>
        <button data-testid="mymotion_orderListAddAdditionalProduct" className={`${styles.buttonSub}`} onClick={() => {props.onProductAdd(props.article.article, props.article.sizes[0], false)}}>{t('additionalProducts.addNow')}</button>
    </div>
}

export function AdditionalProducts(props: AdditionalProductsProps) {
    const {t} = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);

    const eventRef = useRef<HTMLDivElement>(null!);
    const scrollRef = useRef<HTMLDivElement>(null!);
    const canvasRef = useRef<HTMLCanvasElement>(null!);

    const divRefs = [
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!)
    ];

    const filteredProducts = props.library.products.filter((product) => {
        return !props.config.products.some((productState, index) => {
            return (productState.article === product.article);
        });
    });
    const productsPerPage = 4;
    const pageCount = Math.ceil(filteredProducts.length / productsPerPage);
    const startProduct = currentPage * productsPerPage;
    const displayProducts = filteredProducts.slice(startProduct, startProduct + productsPerPage);
    const pages = Array.from({length: pageCount}, (x, i) => i).slice(Math.max(0, currentPage - 2), currentPage + 3);

    // Note(daniel): Needed to correctly align canvas views after the sidebar animation.
    setTimeout(() => {
        if(canvasRef.current) {
            window.dispatchEvent(new Event('resize'));
            canvasRef.current.parentElement?.parentElement?.classList.add(styles.visible);
        }
    }, 1100);
    
    if(displayProducts.length === 0) {
        return null;
    }

    return (
        <div className={styles.container} ref={eventRef}>
            <h1>{t('additionalProducts.header')}</h1>
            <PreviewContainer
                    className={styles.fullCanvas}
                    eventRef={props.eventRef !== undefined ? props.eventRef : eventRef}
                    scrollRef={props.scrollRef !== undefined ? props.scrollRef : scrollRef}
                    canvasRef={canvasRef}
                >
                {
                    displayProducts.map((product, index) => {
                        let article = props.library.products.find((article) => {
                            return article.article === product.article;
                        });
                        if(article === null || article === undefined) {
                            return null;
                        }
                        return <PreviewContainerModel key={index} divRef={divRefs[index]} thumbnail={true} library={props.library} model={article.article} xOffset={0} modelData={article.modelData} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false}/>
                    })
                }
            </PreviewContainer>
            <div className={styles.productContainer}>
            {
                displayProducts.map((product, index) => {
                    return <Product divRef={divRefs[index]} library={props.library} config={props.config} article={product} onProductAdd={props.onProductAdd} index={index} key={index} country={props.ui.country}/>
                })
            }
            </div>
            <div className={styles.pagination}>
                <div className={styles.arrowButton} onClick={() => {
                    if(currentPage > 0) {
                        setCurrentPage(currentPage - 1);
                    }
                }}>
                    <IconArrowLeft/>
                </div>

                <div className={styles.pageContainer}>
                {
                    pages.map((pageIndex) => {
                        return <button key={pageIndex} className={pageIndex === currentPage ? styles.activePage : ''} onClick={() => {
                            setCurrentPage(pageIndex);
                        }}>
                            {pageIndex + 1}
                        </button>
                    })
                }
                </div>
                
                <div className={styles.arrowButton} onClick={() => {
                    if(currentPage < pageCount - 1) {
                        setCurrentPage(currentPage + 1);
                    }
                }}>
                    <IconArrowRight/>
                </div>
            </div>
        </div>
    );
};
